import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Wallet, ShieldCheck, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Scene3D from "@/components/Scene3D";
import LocationsSection from "@/components/LocationsSection";
import Marquee from "@/components/Marquee";
import LiveStats from "@/components/LiveStats";
import { Reveal, MaskedLines, EASE_LUXE } from "@/components/Reveal";
import CompanionCard, { prettyCity } from "@/components/CompanionCard";
import SEO from "@/components/SEO";

const CHAPTERS = [
  { n: "01", title: "Discretion", body: "Every introduction is handled with absolute confidentiality. Your privacy is the foundation of everything we do." },
  { n: "02", title: "Verification", body: "Each companion is personally vetted and verified. What you see is who you meet — no exceptions, no surprises." },
  { n: "03", title: "Refinement", body: "We curate for presence, intellect and grace. Vixera is companionship as an art form, not a transaction." },
];

export default function Home() {
  const [cities, setCities] = useState([]);
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data)).catch(() => {});
    api.get("/companions?featured=true").then((r) => setFeatured(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <SEO
        title="Premium Companionship Directory"
        description="South Africa's invitation-only directory of verified, sophisticated companions across Johannesburg, Cape Town, Durban and Pretoria. Discreet · Verified · Unforgettable."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Vixera",
          url: "https://vixera.net",
          description: "Premium companionship directory for South Africa",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://vixera.net/directory?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden">
        <div className="absolute inset-0 bg-velvet-ink" />
        <Scene3D />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#0C0A0B_78%)]" />

        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-10 mx-auto w-full max-w-[1600px] px-6 pt-24 md:px-12"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 1 }}
            className="mb-8 text-xs uppercase tracking-luxe text-velvet-gold"
          >
            South Africa&rsquo;s premier companionship directory
          </motion.p>

          <MaskedLines
            lines={["Curated", "Companionship,", <span key="r" className="italic text-velvet-rose">Redefined.</span>]}
            lineClassName="font-heading text-[2.25rem] font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[8rem]"
          />

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 1, ease: EASE_LUXE }}
            className="mt-10 flex max-w-xl flex-col gap-8"
          >
            <p className="text-base font-light leading-relaxed text-white/60">
              An invitation-only world of verified, sophisticated companions
              across Johannesburg, Cape Town, Durban and Pretoria.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/directory"
                data-testid="hero-explore-button"
                className="group flex items-center gap-3 rounded-full bg-white px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors duration-500 hover:bg-velvet-gold hover:text-white"
              >
                Explore the directory
                <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/membership"
                data-testid="hero-membership-button"
                className="rounded-full border border-white/20 px-8 py-4 text-xs uppercase tracking-luxe text-white transition-colors duration-500 hover:bg-white hover:text-black"
              >
                Become a member
              </Link>
            </div>

            {/* Live social-proof stats — updates every 30s */}
            <LiveStats className="mt-8" />
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 1 }}
          className="absolute bottom-10 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 text-white/40"
        >
          <span className="text-[10px] uppercase tracking-luxe">Scroll</span>
          <ArrowDown size={14} className="animate-bounce" />
        </motion.div>
      </section>

      <Marquee
        items={["The Vixera Standard", "Premium Companionship", "Discretion Guaranteed", "Verified Elegance"]}
      />

      {/* CITIES */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-40" data-testid="cities-section">
        <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <Reveal>
              <p className="mb-5 text-xs uppercase tracking-luxe text-velvet-gold">Locations</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="max-w-2xl font-heading text-4xl font-semibold leading-[0.95] tracking-tight text-white md:text-6xl">
                Companionship in four <span className="italic text-velvet-gold">electric</span> cities
              </h2>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {cities.map((city, i) => (
            <Reveal key={city.slug} delay={i * 0.08}>
              <Link
                to={`/directory?city=${city.slug}`}
                data-testid={`city-card-${city.slug}`}
                className="group block"
              >
                <div className="clip-frame relative aspect-[3/4] bg-velvet-coal">
                  <img src={city.image} alt={city.name} className="h-full w-full object-cover opacity-70 transition-opacity duration-700 group-hover:opacity-90" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-6">
                    <h3 className="font-heading text-3xl font-light text-white">{city.name}</h3>
                    <p className="mt-1 text-xs uppercase tracking-luxe text-velvet-gold">{city.count} companions</p>
                    <p className="mt-3 text-sm font-light text-white/50">{city.tagline}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <LocationsSection />

      {/* FEATURED SPOTLIGHT */}
      <section className="bg-velvet-cream py-24 md:py-40" data-testid="featured-section">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="mb-16 flex flex-col justify-between gap-6 md:flex-row md:items-end">
            <div>
              <Reveal><p className="mb-5 text-xs uppercase tracking-luxe text-velvet-crimson">The Featured</p></Reveal>
              <Reveal delay={0.1}>
                <h2 className="max-w-2xl font-heading text-4xl font-semibold leading-[0.95] tracking-tight text-velvet-ink md:text-6xl">
                  This season&rsquo;s <span className="italic text-velvet-rose">most wanted</span>
                </h2>
              </Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/directory" className="group flex items-center gap-3 text-xs uppercase tracking-luxe text-velvet-ink transition-colors hover:text-velvet-rose">
                View all <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            {featured.map((c, i) => (
              <CompanionCard key={c.id} companion={c} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ADVERTISER BAND — sensual gold/rose invitation to list */}
      <section className="relative overflow-hidden bg-black py-24 md:py-32" data-testid="advertiser-band">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.5, ease: EASE_LUXE }}
          className="absolute -right-40 top-20 h-96 w-96 rounded-full bg-velvet-rose/25 blur-3xl"
        />
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          whileInView={{ scale: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.8, ease: EASE_LUXE, delay: 0.2 }}
          className="absolute -left-40 bottom-10 h-80 w-80 rounded-full bg-velvet-gold/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-[1600px] px-6 md:px-12">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
              <Reveal>
                <p className="mb-6 text-xs uppercase tracking-luxe text-velvet-gold">For companions &middot; agencies</p>
                <h2 className="font-heading text-5xl font-light leading-[0.95] tracking-tight text-white md:text-7xl">
                  Are you a <span className="italic text-velvet-rose">companion</span>?
                </h2>
                <p className="mt-8 max-w-xl text-base font-light leading-relaxed text-white/60 md:text-lg">
                  List free on South Africa&rsquo;s premium directory. Verified badge,
                  discreet inbox, tour scheduling, and zero commission on your bookings.
                </p>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  {[
                    { icon: Wallet, label: "Keep 100%" },
                    { icon: ShieldCheck, label: "Verified badge" },
                    { icon: Sparkles, label: "Featured boosts" },
                  ].map((v) => {
                    const Icon = v.icon;
                    return (
                      <div key={v.label} className="flex items-center gap-3 border-l-2 border-velvet-gold/40 pl-4">
                        <Icon size={16} className="text-velvet-gold" />
                        <span className="text-xs uppercase tracking-luxe text-white/80">{v.label}</span>
                      </div>
                    );
                  })}
                </div>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="mt-12 flex flex-wrap items-center gap-4">
                  <Link
                    to="/advertise"
                    data-testid="home-advertise-cta"
                    className="group inline-flex items-center gap-3 rounded-full bg-velvet-rose px-8 py-4 text-xs uppercase tracking-luxe text-black transition-colors hover:bg-velvet-gold"
                  >
                    Get listed &mdash; free
                    <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                  </Link>
                  <Link
                    to="/advertise"
                    className="text-xs uppercase tracking-luxe text-white/50 hover:text-velvet-gold"
                  >
                    See how much you could earn &rarr;
                  </Link>
                </div>
              </Reveal>
            </div>

            <Reveal delay={0.2}>
              <div className="border border-velvet-gold/25 bg-velvet-coal/40 p-8 backdrop-blur-sm md:p-12">
                <p className="text-[10px] uppercase tracking-luxe text-velvet-gold">Right now on Vixera</p>
                <div className="mt-6">
                  <LiveStats variant="advertiser" />
                </div>
                <p className="mt-8 text-sm font-light italic leading-relaxed text-white/60">
                  &ldquo;Bookings tripled in my first month. The verification badge alone changed
                  how men message me.&rdquo;
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-luxe text-velvet-gold">— Nadia · Sandton</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="mx-auto max-w-[1600px] px-6 py-24 md:px-12 md:py-48" data-testid="manifesto-section">
        <Reveal>
          <p className="mb-5 text-xs uppercase tracking-luxe text-velvet-gold">The Vixera Standard</p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mb-24 max-w-4xl font-heading text-4xl font-semibold leading-tight tracking-tight text-white md:text-6xl">
            Three principles that set us <span className="italic text-velvet-rose">apart</span> from every other directory.
          </h2>
        </Reveal>

        <div className="space-y-px">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.06}>
              <div className="grid grid-cols-1 gap-6 border-t border-white/10 py-12 md:grid-cols-12 md:gap-12">
                <span className="font-heading text-5xl font-bold text-velvet-rose md:col-span-2 md:text-6xl">{c.n}</span>
                <h3 className="font-heading text-3xl font-semibold text-white md:col-span-4 md:text-4xl">{c.title}</h3>
                <p className="max-w-xl text-base font-light leading-relaxed text-white/50 md:col-span-6">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
